/**
 * "Report this error" on screen (opt-in error reports, behind `__ERROR_REPORTS__`: in every build since MATT GO 2026-10-26
 * 04:58; docs/plans/error-reports.md). Kept apart from the beta aids (./aids.ts, behind `__BETA__`: screen codes, the
 * "Report a problem on this page" link, the test checklist) so a production build carries this and not those.
 */
import { BUILD_ID } from '../diagnostics/buildInfo';
import { errorReportView } from './errorReport';

type Tr = (key: string, params?: Record<string, string | number>) => string;

const CSS = `.error-report>summary{min-block-size:var(--target);box-sizing:border-box}`;

function ensureStyles(): void {
  if (document.getElementById('error-report-css')) return;
  const style = document.createElement('style');
  style.id = 'error-report-css';
  style.textContent = CSS;
  document.head.append(style);
}

const ua = (): string => (typeof navigator === 'undefined' ? '' : navigator.userAgent);

let errorReportSeq = 0;

/**
 * "Report this error" (opt-in error reports; docs/plans/error-reports.md): what an error state appends under its message,
 * behind `if (__ERROR_REPORTS__)`; `err` when the state has it, else the latest error noted (recentErrors.ts). Closed until the user opens it; then the whole report (exactly the email
 * body: the error's listed type, the build, the browser) in a read-only box, "Send by email" (a mailto: the user's own
 * email app sends) and "Copy". The app sends nothing; nothing of the error but its listed type is read (errorReport.ts).
 */
export function errorReportOffer(tr: Tr, err?: unknown): HTMLDetailsElement {
  ensureStyles();
  const id = `error-report-${++errorReportSeq}`;
  const v = errorReportView(tr, BUILD_ID, ua(), err);
  const details = document.createElement('details');
  details.className = 'diagnostics error-report';
  const summary = document.createElement('summary');
  summary.textContent = v.summary;
  const intro = document.createElement('p');
  intro.className = 'help';
  intro.id = `${id}-help`;
  intro.textContent = v.intro;
  const label = document.createElement('label');
  label.htmlFor = id;
  label.textContent = v.label;
  const box = document.createElement('textarea');
  box.id = id;
  box.readOnly = true;
  // Four lines; the version line wraps on a phone, so one more row keeps every line in view.
  box.rows = 5;
  box.dir = 'ltr';
  box.spellcheck = false;
  box.value = v.text;
  box.setAttribute('aria-describedby', `${id}-help`);
  const email = document.createElement('a');
  email.className = 'button secondary';
  email.href = v.href;
  email.textContent = v.email;
  const copy = document.createElement('button');
  copy.type = 'button';
  copy.className = 'secondary';
  copy.textContent = v.copy;
  copy.addEventListener('click', () => {
    // No clipboard (an old browser, an insecure page): the text is selected for the user to copy.
    const select = () => box.select();
    if (navigator.clipboard) navigator.clipboard.writeText(v.text).then(() => (copy.textContent = v.copied), select);
    else select();
  });
  const row = document.createElement('div');
  row.className = 'row';
  row.append(email, copy);
  const help = document.createElement('p');
  help.className = 'help';
  help.textContent = v.emailHelp;
  details.append(summary, intro, label, box, row, help);
  return details;
}
